import type {
  CostUnit,
  QueryCostDataRequest,
  Condition,
  CostDataGroupBy,
  CostGranularity,
  UsageUnit,
} from '@repo/api-gw-sdk';
import { ConditionOperatorEnum, SortOrder } from '@repo/api-gw-sdk';

import { OPERATORS } from '@/components/conditions/OperatorSelect';
import {
  collectValueConditions,
  PropertyPolicyRelation,
  type FilterProperty,
} from '@/types/advanceFilter';
import type { Dayjs } from '@/utils/dayjs';

export const createQueryCostDataRequest = (
  dateRange: {
    startDate: Dayjs;
    endDate: Dayjs;
  },
  groupByField: CostDataGroupBy,
  granularity: CostGranularity,
  query: Condition | undefined,
  costUnit: CostUnit,
  usageUnit: UsageUnit
): QueryCostDataRequest => ({
  timeFrame: {
    startTime: dateRange.startDate.toDate(),
    endTime: dateRange.endDate.toDate(),
  },
  groupBy: groupByField,
  granularity,
  filters: convertQuery(query),
  costUnit,
  usageUnit,
});

export const convertQuery = (
  rootCondition: Condition | undefined
): Record<string, unknown> | undefined => {
  if (!rootCondition?.conditions?.length) {
    return;
  }

  const conditions = collectValueConditions(rootCondition);
  return conditions.reduce<Record<string, unknown>>((acc, condition) => {
    acc[condition.property!] = {
      [convertOperator(condition.operator)]: condition.value,
    };
    return acc;
  }, {});
};

// Maps ConditionOperatorEnum values to the TypeScript property names used by the
// SDK filter classes (e.g. CloudProviderFilters, FreeTextFilters). These are the
// `name` fields in each class's attributeTypeMap, NOT the wire-format `baseName`.
// ObjectSerializer.serialize() handles the name→baseName translation when building
// the HTTP body (e.g. '_in' becomes 'in' on the wire for CloudProviderFilters).
// The mock's applyTokenFilters reads the already-serialized body, so it checks
// the baseName keys ('in', 'notIn', 'contains', ...) — not these intermediate names.
const operatorMap: Partial<Record<ConditionOperatorEnum, string>> = {
  [ConditionOperatorEnum.In]: '_in',
  [ConditionOperatorEnum.NotIn]: 'notIn',
  [ConditionOperatorEnum.Contains]: 'contains',
  [ConditionOperatorEnum.NotContains]: 'notContains',
  [ConditionOperatorEnum.ContainsAnyOf]: 'containsAnyOf',
};

const convertOperator = (operator: string): string =>
  (operator in operatorMap
    ? operatorMap[operator as ConditionOperatorEnum]
    : undefined) ?? operator;

export const convertSort = <T extends string>(
  sort: string | undefined,
  fieldMap: Record<string, T>
): { field: T; order: SortOrder }[] | undefined => {
  if (!sort) return undefined;
  const [fieldStr, orderStr] = sort.split(':');
  const field = fieldMap[fieldStr];
  if (field === undefined) return undefined;
  const order =
    orderStr?.toLowerCase() === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  return [{ field, order }];
};

export const createEmptyCondition = (
  property: FilterProperty,
  value?: string
) => {
  const condition = {
    property: property.name,
    operator: Object.keys(
      OPERATORS[
        property.policyRelation ?? PropertyPolicyRelation.MultipleValues
      ]
    )?.[0],
    value: [] as string[],
  };

  if (value) {
    condition.value.push(value);
  }

  return condition;
};
